<template>
    <div>
        <redesign-data-table-component
            v-if="tableDataMapped?.length && columns?.length"
            :columns="columns"
            :widget-data="widgetData"
            :table-data="tableDataMapped"
            :columns-config="columnsConfig"
            @update-widget="updateWidget"
            @update-columns-config="updateColumnsConfig"
            @update-headers-and-columns-config="updateColumnsAndHeadersConfig"
        >
            <template #cell="{ row, column }">
                <span
                    v-html="row[column.prop]"
                />
            </template>
        </redesign-data-table-component>
        <div
            v-else
            class="flex justify-center"
        >
            <WidgetNoData :widget="widgetData" />
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    ColumnsObjectConfig,
    TableHeadersConfig,
    WidgetExtended,
} from '@/types/dashboard'
import { hasColumnSpecificAlignment } from '@/helpers/alignmentUtils'
import { computed, defineEmits, toRef } from 'vue'
import store from '@/store/store'
import useEntityPositiveNegativeFilters from '@/composables/useEntityPositiveNegativeFilters'
import { Dialer, DialerStatistics, DialerStrategy } from '@/types/dialers'
import RedesignDataTableComponent from '@/components/RedesignBaseComponents/RedesignDataTableComponent.vue'
import WidgetNoData from '@/components/Widgets/WidgetNoData.vue'
import { defaultDialerTableColumns } from '@/enum/dialerRealTimeTableConfigs'
import useTableHeaderTranslations from '@/composables/useTableHeaderTranslations'
import { CAMPAIGN_IVR_LIST_PARAMETER_NAME } from '@/enum/widgetTemplateConfigs'

const props = defineProps<{
    widgetData: WidgetExtended
}>()

const emit = defineEmits<{
    (event: 'update-widget', payload: WidgetExtended): void;
}>()

const { makeTableHeaderTranslations, makeTableHeaderTooltipTranslations } = useTableHeaderTranslations()
const widgetDataRef = toRef(props, 'widgetData')

/*Computed*/
const allDialersWithData = computed(() => {
    if (!filteredDialers.value.length) {
        return []
    }
    return filteredDialers.value.filter(el => el.data && Object.keys(el.data).length)
})

const dialerWithTypeIVR = computed<Dialer[]>(() => {
    return store.getters['dialers/getAllDialersWithTypeIVR'] || []
})

const tableDataMapped = computed(() => {
    if (!allDialersWithData.value.length) {
        return []
    }
    return allDialersWithData.value.map((row) => {
        const additionalDialerStrategyColumns: Partial<Record<keyof DialerStrategy, string | number>> = {}
        const additionalCallStatisticsColumns: Partial<Record<keyof DialerStatistics, string | number>> = {}
        if (row.data.DialerStrategy) {
            Object.entries(row.data.DialerStrategy).forEach(([ key, value ]) => {
                const k = key as keyof DialerStrategy
                additionalDialerStrategyColumns[k] = value
            })

        }
        if (row.data.CallStatistics ) {
            Object.entries(row.data.CallStatistics).forEach(([ key, value ]) => {
                const k = key as keyof DialerStatistics
                additionalCallStatisticsColumns[k] = value
            })
        }

        return {
            campaignName: row.name,
            campaignType: row.type,
            campaignID: row.campaignID,
            ...additionalDialerStrategyColumns,
            ...additionalCallStatisticsColumns
        }
    })

})

const columns = defaultDialerTableColumns.map((column) => {
    const baseLabel = column.label as string
    return {
        ...column,
        label: makeTableHeaderTranslations(column, baseLabel),
        tooltipLabel: makeTableHeaderTooltipTranslations(column, baseLabel)
    }
})

const columnsConfig = computed(() => {
    if (props.widgetData.WidgetLayout?.columnsObjectConfig && Object.keys(props.widgetData.WidgetLayout.columnsObjectConfig).length) {
        return props.widgetData.WidgetLayout.columnsObjectConfig
    } else {
        return {}
    }
})


const { filteredItems: filteredDialers } = useEntityPositiveNegativeFilters(
    widgetDataRef,
    CAMPAIGN_IVR_LIST_PARAMETER_NAME,
    dialerWithTypeIVR,
    'campaignID'
)

/*Methods*/
const updateColumnsConfig = (config: ColumnsObjectConfig) => {
    if (!config || !Object.values(config)) {
        return
    }
    const hasColumnSpecificAlignmentValue = hasColumnSpecificAlignment(config, props.widgetData.WidgetLayout?.tableColumnsAlignment)
    const updatedWidgetData = {
        ...props.widgetData,
        WidgetLayout: {
            ...(props.widgetData.WidgetLayout || {}),
            hasColumnSpecificAlignment: hasColumnSpecificAlignmentValue,
            columnsObjectConfig: config
        }
    }
    updateWidget(updatedWidgetData)
}

const updateColumnsAndHeadersConfig = (config: ColumnsObjectConfig, tableHeadersConfig: TableHeadersConfig) => {
    const hasColumnSpecificAlignmentValue = hasColumnSpecificAlignment(config, props.widgetData.WidgetLayout?.tableColumnsAlignment)
    const updatedWidgetData = {
        ...props.widgetData,
        WidgetLayout: {
            ...(props.widgetData.WidgetLayout || {}),
            hasColumnSpecificAlignment: hasColumnSpecificAlignmentValue,
            ...(config && Object.values(config) ? { columnsObjectConfig: config } : {}),
            ...(tableHeadersConfig ? { tableHeadersConfig } : {})
        }
    }
    updateWidget(updatedWidgetData)
}
const updateWidget = (newData: WidgetExtended) => {
    emit('update-widget', newData)
}
</script>

<style scoped lang="scss">

</style>
